import { Button, Card, ConfigProvider, Tabs, Tooltip } from 'antd'
import { Text } from 'designSystem/components/Text'
import { anchorBelongsToZone } from 'features/libra/comments/anchorMatching'
import { useReveal } from 'features/libra/comments/CommentsProvider'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import {
  NiiSaveAlert,
  NiiSaveButton,
} from 'features/libra/stepComponents/niiReport/NiiSaveBanner'
import { NiiStrategy } from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import { getZoneCardTab } from 'features/libra/stepComponents/zoneAnalysis/useZonePropertyAnchor'
import {
  ALL_ZONE_TAB_KEYS,
  DamageMechanism,
  ZoneParams,
  ZoneTabKey,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  clearTouchedPrefill,
  getVisibleZonePrefillTabs,
  ZonePrefillDerivationContext,
  ZoneTabAgentPermissions,
} from 'features/libra/stepComponents/zoneAnalysis/zoneTabAutofill'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { ExternalLink, GripHorizontal, X } from 'lucide-react'
import type { MouseEvent as ReactMouseEvent } from 'react'
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import type {
  HandleComponent,
  RndDragCallback,
  RndResizeCallback,
} from 'react-rnd'
import { Rnd } from 'react-rnd'
import styled from 'styled-components'

import { DamageMechanismsPanel } from './DamageMechanismsPanel'
import { DesignFunctionPanel } from './DesignFunctionPanel'
import { NiiStrategyPanel } from './NiiStrategyPanel'
import { RbaInformationPanel } from './RbaInformationPanel'
import {
  fitZoneCardRect,
  moveZoneCardRect,
  resizeZoneCardRect,
  type ZoneCardRect,
  type ZoneCardResizeCorner,
} from './zoneCardGeometry'
import { ZoneDescriptionPanel } from './ZoneDescriptionPanel'
import { ZoneSummaryCard } from './ZoneSummaryCard'
import { ZonePrefillAllBar, ZoneTabAiPrefillBanner } from './ZoneTabAiPrefill'

let cardOpenCount = 0
let topZIndex = 1100
const RIGHT_MARGIN = 16
const CASCADE_STEP = 40
const MIN_WIDTH = 480
const MIN_HEIGHT = 300
const DEFAULT_WIDTH = 760
const TOP_OFFSET = 64 // clear top nav bar

const FloatingRnd = styled(Rnd)<{ $zIndex: number }>`
  position: fixed !important;
  top: 0;
  left: 0;
  z-index: ${({ $zIndex }) => $zIndex};
  overflow: visible;

  .zone-card-resize-top-left {
    top: 0 !important;
    left: 0 !important;
  }

  .zone-card-resize-bottom-left {
    bottom: 0 !important;
    left: 0 !important;
  }

  .zone-card-resize-bottom-right {
    right: 0 !important;
    bottom: 0 !important;
  }
`

const FloatingCard = styled(Card)<{ $zoneColor: string }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.4),
    0 4px 16px rgba(0, 0, 0, 0.25);
  background: ${({ theme }) => theme.forecast.background1};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 10px;

  .zone-card-header {
    flex: 0 0 auto;
    padding: 0 calc(6 * var(--spacing));
    background: ${({ theme }) => theme.forecast.background2};
    border-bottom: 2px solid ${({ $zoneColor }) => $zoneColor};
  }

  .zone-card-title {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    min-width: 0;
    width: 100%;
    padding: var(--spacing) 0;
    gap: 0.5rem;
  }

  .zone-card-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
  }

  .zone-card-actions {
    flex-shrink: 0;
  }

  .zone-card-actions li {
    width: 100% !important;
    margin: 0;
  }
`

const TitleContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 24px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`

const TitleStart = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const TitleActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
`

const SaveZoneHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.25rem;
  width: 100%;
  cursor: default;
`

const ZoneColorDot = styled.div<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`

const TitleText = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TabsContainer = styled.div`
  flex: 1;

  .ant-tabs-tabpane {
    padding: 12px 0 0 0;
  }
`

const MoveButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(100% - 40px);
  height: 20px;
  margin: 0 20px;
  padding: 0;
  border: 0;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  background: ${({ theme }) => theme.forecast.background2};
  cursor: grab;
  user-select: none;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3} !important;
  }

  &:active {
    cursor: grabbing;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.primary};
    outline-offset: -2px;
  }
`

const ResizeHandle = styled(Button)<{ $corner: ZoneCardResizeCorner }>`
  width: 100%;
  height: 100%;
  min-width: 0;
  padding: 0;
  border: 0;
  cursor: ${({ $corner }) =>
    $corner === 'bottom-left' ? 'nesw-resize' : 'nwse-resize'};
  background-color: transparent;
  background-image: repeating-linear-gradient(
    ${({ $corner }) => ($corner === 'bottom-left' ? '45deg' : '-45deg')},
    transparent,
    transparent 2px,
    ${({ theme }) => theme.forecast.tertiaryText} 2px,
    ${({ theme }) => theme.forecast.tertiaryText} 3px
  );
  opacity: 0.6;

  &:hover {
    background-color: transparent !important;
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.primary};
    outline-offset: -2px;
  }
`
const getZoneCardArrowDelta = (key: string) => {
  switch (key) {
    case 'ArrowLeft':
      return { x: -1, y: 0 }
    case 'ArrowRight':
      return { x: 1, y: 0 }
    case 'ArrowUp':
      return { x: 0, y: -1 }
    case 'ArrowDown':
      return { x: 0, y: 1 }
    default:
      return undefined
  }
}

export interface ZoneFloatingCardProps {
  zone: ZoneParams
  onClose: () => void
  onChange?: (updates: Partial<ZoneParams>) => void
  onSaveZone?: () => Promise<void>
  isZoneDirty?: boolean
  isSaving?: boolean
  savingZoneId?: string
  saveError?: string
  readonly?: boolean
  visibleTabs?: ZoneTabKey[]
  zoneTabAgentPermissions: ZoneTabAgentPermissions
  prefillDerivationContext?: ZonePrefillDerivationContext
}

const resizeCornerForDirection = (
  direction: Parameters<RndResizeCallback>[1]
): ZoneCardResizeCorner | undefined => {
  switch (direction) {
    case 'topLeft':
      return 'top-left'
    case 'bottomLeft':
      return 'bottom-left'
    case 'bottomRight':
      return 'bottom-right'
    default:
      return undefined
  }
}

export const ZoneFloatingCard: FC<ZoneFloatingCardProps> = ({
  zone,
  onClose,
  onChange,
  onSaveZone,
  isZoneDirty = false,
  isSaving = false,
  savingZoneId,
  saveError,
  readonly,
  visibleTabs = ALL_ZONE_TAB_KEYS,
  zoneTabAgentPermissions,
  prefillDerivationContext,
}) => {
  const { selectedDocumentId, setSelectedDocumentId } = useLibraDocument()

  const isOnZoneDocument = selectedDocumentId === zone.zone_reference_image_id

  // Following a link to a comment in this card switches to the tab that
  // renders it.
  const stepId = useActiveStepId()
  const [activeTab, setActiveTab] = useState<string>(visibleTabs[0])
  useReveal((anchor) => {
    const tab = getZoneCardTab(anchor)
    if (!tab || !visibleTabs.includes(tab)) return
    if (anchorBelongsToZone(anchor, stepId, zone.id)) setActiveTab(tab)
  })

  const [rect, setRect] = useState<ZoneCardRect>(() => {
    const idx = cardOpenCount % 5
    cardOpenCount++
    return fitZoneCardRect(
      {
        x:
          window.innerWidth - DEFAULT_WIDTH - RIGHT_MARGIN - idx * CASCADE_STEP,
        y: TOP_OFFSET,
        width: DEFAULT_WIDTH,
        height: Math.max(0, window.innerHeight - TOP_OFFSET),
      },
      { width: window.innerWidth, height: window.innerHeight }
    )
  })

  const [zIndex, setZIndex] = useState(() => ++topZIndex)

  const bringToFront = useCallback(() => {
    setZIndex((current) => {
      if (current === topZIndex) return current
      return ++topZIndex
    })
  }, [])

  useEffect(() => {
    return () => {
      cardOpenCount = Math.max(0, cardOpenCount - 1)
    }
  }, [])

  const rectRef = useRef(rect)
  const rndRef = useRef<Rnd>(null)
  const updateRect = useCallback((nextRect: ZoneCardRect) => {
    rectRef.current = nextRect
    setRect(nextRect)
  }, [])
  const resizeDeltaRef = useRef({ width: 0, height: 0 })
  const resizeMinimumRef = useRef<{ width: number; height: number } | null>(
    null
  )
  const [resizeMinimum, setResizeMinimum] = useState<{
    width: number
    height: number
  } | null>(null)

  // Rnd ignores controlled position during resize, so its gesture updates can
  // overwrite viewport-fitted geometry; reconcile after commit.
  useLayoutEffect(() => {
    rndRef.current?.updatePosition({ x: rect.x, y: rect.y })
    rndRef.current?.updateSize({ width: rect.width, height: rect.height })
  }, [rect])

  useEffect(() => {
    const handleViewportResize = () => {
      const next = fitZoneCardRect(rectRef.current, {
        width: window.innerWidth,
        height: window.innerHeight,
      })
      const minimum = resizeMinimumRef.current
      if (minimum) {
        const nextMinimum = {
          width: Math.min(minimum.width, next.width),
          height: Math.min(minimum.height, next.height),
        }
        resizeMinimumRef.current = nextMinimum
        setResizeMinimum(nextMinimum)
      }
      updateRect(next)
    }

    window.addEventListener('resize', handleViewportResize)
    return () => window.removeEventListener('resize', handleViewportResize)
  }, [updateRect])

  const handleRndDrag = useCallback<RndDragCallback>(
    (_event, data) => {
      updateRect(
        moveZoneCardRect(rectRef.current, data.deltaX, data.deltaY, {
          width: window.innerWidth,
          height: window.innerHeight,
        })
      )
    },
    [updateRect]
  )

  const handleRndResize = useCallback<RndResizeCallback>(
    (_event, direction, _element, delta) => {
      const corner = resizeCornerForDirection(direction)
      if (!corner) return

      const previous = resizeDeltaRef.current
      resizeDeltaRef.current = delta
      const resizeFromLeft = corner !== 'bottom-right'
      const resizeFromTop = corner === 'top-left'
      const minimum = resizeMinimumRef.current

      updateRect(
        resizeZoneCardRect(
          rectRef.current,
          (resizeFromLeft ? -1 : 1) * (delta.width - previous.width),
          (resizeFromTop ? -1 : 1) * (delta.height - previous.height),
          corner,
          { width: window.innerWidth, height: window.innerHeight },
          minimum?.width ?? MIN_WIDTH,
          minimum?.height ?? MIN_HEIGHT
        )
      )
    },
    [updateRect]
  )

  const handleRndResizeStart = useCallback(() => {
    bringToFront()
    resizeDeltaRef.current = { width: 0, height: 0 }
    const minimum = {
      width: Math.min(MIN_WIDTH, rectRef.current.width),
      height: Math.min(MIN_HEIGHT, rectRef.current.height),
    }
    resizeMinimumRef.current = minimum
    setResizeMinimum(minimum)
  }, [bringToFront])

  const handleRndResizeStop = useCallback(() => {
    resizeMinimumRef.current = null
    setResizeMinimum(null)
  }, [])

  const handleMoveKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const delta = getZoneCardArrowDelta(e.key)
    if (!delta) return
    e.preventDefault()
    bringToFront()
    const step = e.shiftKey ? 50 : 10
    updateRect(
      moveZoneCardRect(rectRef.current, delta.x * step, delta.y * step, {
        width: window.innerWidth,
        height: window.innerHeight,
      })
    )
  }

  const startResizeFromKeyboard = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    corner: ZoneCardResizeCorner
  ) => {
    const delta = getZoneCardArrowDelta(e.key)
    if (!delta) return
    e.preventDefault()
    bringToFront()
    const step = e.shiftKey ? 50 : 10
    updateRect(
      resizeZoneCardRect(
        rectRef.current,
        delta.x * step,
        delta.y * step,
        corner,
        { width: window.innerWidth, height: window.innerHeight },
        MIN_WIDTH,
        MIN_HEIGHT
      )
    )
  }

  const handleBottomLeftResizeKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>
  ) => startResizeFromKeyboard(e, 'bottom-left')
  const handleBottomRightResizeKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>
  ) => startResizeFromKeyboard(e, 'bottom-right')
  const handleTopLeftResizeKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>
  ) => startResizeFromKeyboard(e, 'top-left')

  const handleUpdate = useCallback(
    (updates: Partial<ZoneParams>) => {
      // "AI suggested until touched": a human edit takes ownership of the
      // edited fields, dropping their AI highlight and citation. AI-applied
      // updates carry their own `ai_prefill` and are passed through untouched.
      const prefillPatch = clearTouchedPrefill(zone, updates)
      onChange?.(prefillPatch ? { ...updates, ...prefillPatch } : updates)
    },
    [onChange, zone]
  )

  const handleSaveZone = useCallback(() => {
    if (onSaveZone) void onSaveZone().catch(() => {})
  }, [onSaveZone])

  const handleDamageMechanismsChange = useCallback(
    (mechanisms: DamageMechanism[]) => {
      handleUpdate({ damage_mechanisms: mechanisms })
    },
    [handleUpdate]
  )

  const handleStrategyChange = useCallback(
    (strategy: NiiStrategy) => {
      handleUpdate({ nii_strategy: strategy })
    },
    [handleUpdate]
  )

  const popupTheme = useMemo(
    () => ({ token: { zIndexPopupBase: zIndex + 1 } }),
    [zIndex]
  )

  const zoneColor = zone.color || '#3B82F6'
  const prefillTabs = useMemo(
    () => getVisibleZonePrefillTabs(visibleTabs),
    [visibleTabs]
  )
  const prefillAllAgentPermissions = useMemo(
    () =>
      Array.from(
        new Set(
          prefillTabs.flatMap((tab) => zoneTabAgentPermissions[tab] ?? [])
        )
      ),
    [prefillTabs, zoneTabAgentPermissions]
  )

  const tabItems = useMemo(() => {
    const registry: Record<
      ZoneTabKey,
      { key: ZoneTabKey; label: string; children: ReactNode }
    > = {
      basic: {
        key: 'basic',
        label: 'Basic Info',
        children: (
          <>
            <ZoneTabAiPrefillBanner
              zone={zone}
              tab='basic_info'
              onApply={handleUpdate}
              agentPermissions={zoneTabAgentPermissions.basic_info ?? []}
              derivationContext={prefillDerivationContext}
              readonly={readonly}
            />
            <ZoneDescriptionPanel
              zone={zone}
              onChange={handleUpdate}
              readonly={readonly}
            />
          </>
        ),
      },
      design: {
        key: 'design',
        label: 'Design & Function',
        children: (
          <>
            <ZoneTabAiPrefillBanner
              zone={zone}
              tab='design_function'
              onApply={handleUpdate}
              agentPermissions={zoneTabAgentPermissions.design_function ?? []}
              derivationContext={prefillDerivationContext}
              readonly={readonly}
            />
            <DesignFunctionPanel
              zone={zone}
              onChange={handleUpdate}
              readonly={readonly}
            />
          </>
        ),
      },
      damage: {
        key: 'damage',
        label: 'Damage Mechanisms',
        children: (
          <>
            <ZoneTabAiPrefillBanner
              zone={zone}
              tab='damage_mechanisms'
              onApply={handleUpdate}
              agentPermissions={zoneTabAgentPermissions.damage_mechanisms ?? []}
              derivationContext={prefillDerivationContext}
              readonly={readonly}
            />
            <DamageMechanismsPanel
              mechanisms={zone.damage_mechanisms ?? []}
              onChange={handleDamageMechanismsChange}
              readonly={readonly}
              zoneId={zone.id}
              aiPrefill={zone.ai_prefill?.damage_mechanisms}
            />
          </>
        ),
      },
      strategy: {
        key: 'strategy',
        label: 'NII Strategy',
        children: (
          <>
            <ZoneTabAiPrefillBanner
              zone={zone}
              tab='nii_strategy'
              onApply={handleUpdate}
              agentPermissions={zoneTabAgentPermissions.nii_strategy ?? []}
              derivationContext={prefillDerivationContext}
              readonly={readonly}
            />
            <NiiStrategyPanel
              strategy={zone.nii_strategy}
              onChange={handleStrategyChange}
              readonly={readonly}
              zoneId={zone.id}
              aiPrefill={zone.ai_prefill?.nii_strategy}
            />
          </>
        ),
      },
      rba: {
        key: 'rba',
        label: 'Risk Assessment',
        children: (
          <>
            <ZoneTabAiPrefillBanner
              zone={zone}
              tab='risk_assessment'
              onApply={handleUpdate}
              agentPermissions={zoneTabAgentPermissions.risk_assessment ?? []}
              derivationContext={prefillDerivationContext}
              readonly={readonly}
            />
            <RbaInformationPanel
              zone={zone}
              onChange={handleUpdate}
              readonly={readonly}
            />
          </>
        ),
      },
    }
    return visibleTabs.map((key) => registry[key])
  }, [
    zone,
    handleUpdate,
    handleDamageMechanismsChange,
    handleStrategyChange,
    readonly,
    visibleTabs,
    zoneTabAgentPermissions,
    prefillDerivationContext,
  ])

  return createPortal(
    <ConfigProvider getPopupContainer={() => document.body} theme={popupTheme}>
      <FloatingRnd
        ref={rndRef}
        $zIndex={zIndex}
        position={{ x: rect.x, y: rect.y }}
        size={{ width: rect.width, height: rect.height }}
        dragHandleClassName='zone-card-drag-handle'
        cancel='button:not(.zone-card-drag-handle)'
        enableResizing={{ topLeft: true, bottomLeft: true, bottomRight: true }}
        minWidth={resizeMinimum?.width ?? Math.min(MIN_WIDTH, rect.width)}
        minHeight={resizeMinimum?.height ?? Math.min(MIN_HEIGHT, rect.height)}
        resizeHandleClasses={{
          topLeft: 'zone-card-resize-top-left',
          bottomLeft: 'zone-card-resize-bottom-left',
          bottomRight: 'zone-card-resize-bottom-right',
        }}
        resizeHandleComponent={
          {
            topLeft: (
              <ResizeHandle
                type='text'
                size='small'
                $corner='top-left'
                aria-label='Resize zone card from top left'
                aria-keyshortcuts='ArrowUp ArrowDown ArrowLeft ArrowRight'
                title='Resize from top left with the arrow keys; hold Shift for larger steps'
                onKeyDown={handleTopLeftResizeKeyDown}
                onFocus={bringToFront}
              />
            ),
            bottomLeft: (
              <ResizeHandle
                type='text'
                size='small'
                $corner='bottom-left'
                aria-label='Resize zone card from bottom left'
                aria-keyshortcuts='ArrowUp ArrowDown ArrowLeft ArrowRight'
                title='Resize from bottom left with the arrow keys; hold Shift for larger steps'
                onKeyDown={handleBottomLeftResizeKeyDown}
                onFocus={bringToFront}
              />
            ),
            bottomRight: (
              <ResizeHandle
                type='text'
                size='small'
                $corner='bottom-right'
                aria-label='Resize zone card from bottom right'
                aria-keyshortcuts='ArrowUp ArrowDown ArrowLeft ArrowRight'
                title='Resize from bottom right with the arrow keys; hold Shift for larger steps'
                onKeyDown={handleBottomRightResizeKeyDown}
                onFocus={bringToFront}
              />
            ),
          } satisfies HandleComponent
        }
        onMouseDown={bringToFront}
        onClick={(event: ReactMouseEvent<HTMLDivElement>) =>
          event.stopPropagation()
        }
        onDragStart={bringToFront}
        onDrag={handleRndDrag}
        onResizeStart={handleRndResizeStart}
        onResize={handleRndResize}
        onResizeStop={handleRndResizeStop}>
        <FloatingCard
          $zoneColor={zoneColor}
          size='small'
          variant='outlined'
          title={
            <>
              <TitleContent>
                <TitleStart>
                  <ZoneColorDot $color={zoneColor} />
                  <TitleText>
                    {readonly ? 'Zone Details' : 'Zone Analysis'}: {zone.title}
                  </TitleText>
                </TitleStart>
                <DragHandle>
                  <GripHorizontal size={14} aria-hidden='true' />
                </DragHandle>
                <TitleActions>
                  {!isOnZoneDocument && zone.zone_reference_image_id && (
                    <Tooltip title="Navigate to this zone's reference image">
                      <Button
                        size='small'
                        icon={<ExternalLink size={12} aria-hidden='true' />}
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedDocumentId(zone.zone_reference_image_id!)
                        }}>
                        Go to Document
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title='Close'>
                    <Button
                      type='text'
                      size='small'
                      aria-label='Close zone card'
                      icon={<X size={16} aria-hidden='true' />}
                      onClick={(e) => {
                        e.stopPropagation()
                        onClose()
                      }}
                    />
                  </Tooltip>
                </TitleActions>
              </TitleContent>
              {onSaveZone && !readonly && !zone.ai_suggested && (
                <SaveZoneHeader>
                  <NiiSaveAlert
                    title={isZoneDirty ? 'Save Changes?' : 'No Changes'}
                    type={isZoneDirty ? 'warning' : 'success'}
                    showIcon
                    action={
                      <NiiSaveButton
                        type='primary'
                        size='small'
                        loading={isSaving && savingZoneId === zone.id}
                        disabled={!isZoneDirty || isSaving}
                        onClick={handleSaveZone}>
                        {isSaving && savingZoneId === zone.id
                          ? 'Saving…'
                          : 'Save zone'}
                      </NiiSaveButton>
                    }
                  />
                  <Text variant='caption' color='muted' wrap='normal'>
                    Saves changes in this popup only. Changes elsewhere on the
                    page remain unsaved.
                  </Text>
                  {saveError && (
                    <Text
                      variant='caption'
                      color='destructive'
                      wrap='normal'
                      role='alert'>
                      {saveError}
                    </Text>
                  )}
                </SaveZoneHeader>
              )}
            </>
          }
          classNames={{
            header: 'zone-card-header zone-card-drag-handle',
            title: 'zone-card-title',
            body: 'zone-card-body',
            actions: 'zone-card-actions',
          }}
          actions={[
            <MoveButton
              key='move'
              type='text'
              size='small'
              /* eslint-disable-next-line no-classname/no-classname-outside-designsystem -- react-rnd needs this stable drag selector. */
              className='zone-card-drag-handle'
              aria-label='Move zone card'
              aria-keyshortcuts='ArrowUp ArrowDown ArrowLeft ArrowRight'
              title='Drag to move; use arrow keys to move 10 pixels or Shift plus an arrow to move 50 pixels'
              onKeyDown={handleMoveKeyDown}
              onFocus={bringToFront}>
              <GripHorizontal size={14} aria-hidden='true' />
            </MoveButton>,
          ]}
          onMouseDown={bringToFront}>
          <ZoneSummaryCard zone={zone} />
          <ZonePrefillAllBar
            zone={zone}
            tabs={prefillTabs}
            onApply={handleUpdate}
            agentPermissions={prefillAllAgentPermissions}
            derivationContext={prefillDerivationContext}
            readonly={readonly}
          />
          <TabsContainer>
            <Tabs
              items={tabItems}
              size='small'
              activeKey={activeTab}
              onChange={setActiveTab}
            />
          </TabsContainer>
        </FloatingCard>
      </FloatingRnd>
    </ConfigProvider>,
    document.body
  )
}
